import type { ReactNode } from 'react';

import { useSortable } from '@dnd-kit/react/sortable';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import { DraggableIcon } from '../../../../images/shapes';

const sortableTransition = {
  duration: 300,
  easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
  idle: true,
};

export interface DraggableCardProps {
  /**
   * Unique section identifier used by the sortable list.
   */
  id: string;
  /**
   * Position of the card in the sections order.
   */
  index: number;
  /**
   * Callback hiding the section with the given id.
   */
  hideSection: (sectionId: string) => void;
  /**
   * Whether the dashboard is in edit mode (enables dragging and hiding).
   */
  dashboardEditing: boolean;
  /**
   * Section content rendered inside the card.
   */
  children?: ReactNode;
}

export const DraggableCard = ({
  id,
  index,
  hideSection,
  dashboardEditing,
  children,
}: DraggableCardProps) => {
  const { t } = useTranslation();
  const { isDragging, ref, handleRef } = useSortable({
    id,
    index,
    disabled: !dashboardEditing,
    transition: sortableTransition,
  });

  if (!dashboardEditing) {
    return <>{children}</>;
  }

  return (
    <div
      className={twMerge(
        'p-4 border border-dashed rounded-md border-slate-400 relative cursor-grab dark:border-slate-700',
        'active:cursor-grabbing',
        isDragging && 'contain-[layout_paint] opacity-75',
      )}
      data-shadow={isDragging || undefined}
      ref={(element) => {
        ref(element);
        handleRef(element);
      }}
      onPointerDownCapture={(event) => {
        if ((event.target as HTMLElement).closest('[data-no-drag]')) {
          event.stopPropagation();
        }
      }}
    >
      {children}
      <div className="absolute right-2 top-2 flex gap-1">
        <button
          className="border border-slate-300 rounded-md py-1.5 px-2
           text-slate-500 bg-white text-center cursor-pointer hover:bg-slate-100
            dark:bg-slate-900 dark:border-slate-700 dark:hover:bg-slate-800 dark:text-slate-400"
          onClick={() => hideSection(id)}
          data-no-drag
        >
          {t('Home.DashboardCustomization.Hide')}
        </button>
        <button
          type="button"
          className="pointer-events-none rounded-md border border-slate-300 bg-white px-2 py-1.5
          dark:bg-slate-900 dark:border-slate-700 dark:hover:bg-slate-800"
        >
          <DraggableIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default DraggableCard;
